import chunk from "lodash/chunk";

export type ICDLServiceImplFactory = ReturnType<typeof CDLServiceImplFactory>;

export const VITALS_BATCH_SIZE = 10;

const getLatestVital = (items: IParsedCDLItem[] = []): ICDLLatestVital => {
  const latest = items
    .map((item) => ({ date: item.dt, value: Number(item.quant2) }))
    .filter((item) => !isNaN(item.value))
    .sort((a, b) => a.date.localeCompare(b.date))
    .pop();

  return latest
    ? { avg: latest.value, date: latest.date }
    : { avg: null, date: null };
};

export const CDLServiceImplFactory = ({
  cdlRepository,
}: {
  cdlRepository: ICDLRepository;
}) => {
  const getPatientGraphData: ICDLService["getPatientGraphData"] = async (
    payload
  ) => {
    const response = await cdlRepository.getPatientDataFromCDL({
      patient_id: payload.patient_id,
      date_from: payload.dateRange.from,
      date_to: payload.dateRange.to,
    });
    return response;
  };

  const getPatientDailyRRData: ICDLService["getPatientDailyRRData"] = async (
    payload
  ) => {
    const response = await cdlRepository.getPatientDailyRRData({
      patient_id: payload.patient_id,
      date_from: payload.dateRange.from,
      date_to: payload.dateRange.to,
    });
    return response;
  };

  const getPatientDailyHRData: ICDLService["getPatientDailyHRData"] = async (
    payload
  ) => {
    const response = await cdlRepository.getPatientDailyHRData({
      patient_id: payload.patient_id,
      date_from: payload.dateRange.from,
      date_to: payload.dateRange.to,
    });
    return response;
  };

  const getPatientHourlyRRData: ICDLService["getPatientHourlyRRData"] = async (
    payload
  ) => {
    const response = await cdlRepository.getPatientHourlyRRData({
      patient_id: payload.patient_id,
      date_from: payload.dateRange.from,
      date_to: payload.dateRange.to,
    });
    return response;
  };

  const getPatientHourlyHRData: ICDLService["getPatientHourlyHRData"] = async (
    payload
  ) => {
    const response = await cdlRepository.getPatientHourlyHRData({
      patient_id: payload.patient_id,
      date_from: payload.dateRange.from,
      date_to: payload.dateRange.to,
    });
    return response;
  };

  const getPatientMinutelyRRData: ICDLService["getPatientMinutelyRRData"] =
    async (payload) => {
      const response = await cdlRepository.getPatientMinutelyRRData({
        patient_id: payload.patient_id,
        date_from: payload.dateRange.from,
        date_to: payload.dateRange.to,
      });
      return response;
    };

  const getPatientMinutelyHRData: ICDLService["getPatientMinutelyHRData"] =
    async (payload) => {
      const response = await cdlRepository.getPatientMinutelyHRData({
        patient_id: payload.patient_id,
        date_from: payload.dateRange.from,
        date_to: payload.dateRange.to,
      });
      return response;
    };

  const getBedExitMotionData: ICDLService["getBedExitMotionData"] = async (
    payload
  ) => {
    const response = await cdlRepository.getBedExitMotionData({
      patient_id: payload.patient_id,
      date_from: payload.dateRange.from,
      date_to: payload.dateRange.to,
    });
    return response;
  };

  const getHeartRateData: ICDLService["getHeartRateData"] = async (payload) => {
    const response = await cdlRepository.getHeartRateData({
      patient_id: payload.patient_id,
      date_from: payload.dateRange.from,
      date_to: payload.dateRange.to,
    });
    return response;
  };

  const getLatestVitalsForPatients: ICDLService["getLatestVitalsForPatients"] =
    async ({ patientIds, metric, granularity, dateRange, signal, onChunk }) => {
      const latestVitals: { [patientId: string]: ICDLLatestVital } = {};
      let hasFailedChunk = false;

      for (const patientIdsChunk of chunk(patientIds, VITALS_BATCH_SIZE)) {
        if (signal?.aborted) break;

        const response = await cdlRepository.getPatientsVitalsBatch({
          patient_ids: patientIdsChunk,
          metric,
          granularity,
          date_from: dateRange.from,
          date_to: dateRange.to,
          signal,
        });

        if (signal?.aborted) break;
        if (!response.success || !response.data) {
          hasFailedChunk = true;
          continue;
        }

        const chunkVitals: { [patientId: string]: ICDLLatestVital } = {};
        patientIdsChunk.forEach((patientId) => {
          chunkVitals[patientId] = getLatestVital(response.data?.[patientId]);
        });

        Object.assign(latestVitals, chunkVitals);
        onChunk?.(chunkVitals);
      }

      if (signal?.aborted) {
        return { success: false, error: { message: "Request was aborted" } };
      }
      if (hasFailedChunk && Object.keys(latestVitals).length === 0) {
        return { success: false, error: { message: "Something went wrong" } };
      }
      return { success: true, data: latestVitals };
    };

  return {
    getPatientGraphData,
    getBedExitMotionData,
    getHeartRateData,
    getLatestVitalsForPatients,
    getPatientDailyRRData,
    getPatientDailyHRData,
    getPatientHourlyRRData,
    getPatientHourlyHRData,
    getPatientMinutelyRRData,
    getPatientMinutelyHRData,
  };
};
